/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Imgix from 'js/components/Imgix';

import { Grid, Link, Typography2, breakpoints } from '@coursera/cds-core';

import EnterpriseSkillPathwayEnrollButtonNotConfigurable from 'bundles/enterprise-learning-path-description-page/components/EnterpriseSkillPathwayEnrollButtonNotConfigurable';
import IncludedSection from 'bundles/enterprise-learning-path-description-page/components/product-overview/IncludedSection';
import ToolsSection from 'bundles/enterprise-learning-path-description-page/components/product-overview/ToolsSection';
import SkillPathwayCareerBandKeyDetailsItem from 'bundles/enterprise-learning-path-description-page/components/syllabus/SkillPathwayCareerBandKeyDetailsItem';
import { useSkillPathway } from 'bundles/enterprise-learning-path-description-page/contexts/SkillPathwayContext';
import LearningObjectives from 'bundles/enterprise-learning-path-description-page/modules/LearningObjectives';
import type { EnterpriseLearningPathPageFieldsFragment } from 'bundles/enterprise-learning-path-description-page/queries/__generated__/EnterpriseLearningPathPageQuery';
import { SKILL_PATHWAY_NAV_ITEMS } from 'bundles/enterprise-learning-path-description-page/utils/getPageNavigationLinks';
import {
  getAssessmentsDataFromVspPageData,
  getInstructorsFromVspPageData,
} from 'bundles/enterprise-learning-path-description-page/utils/vspXdpV2Utils';
import EnterpriseExperiments from 'bundles/epic/clients/Enterprise';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import HeroInstructors from 'bundles/unified-description-page-common/components/modules/hero/HeroInstructors';
import HeroModule from 'bundles/unified-description-page-common/components/modules/hero/HeroModule';
import KeyInformationDetailsItem from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationDetailsItem';
import KeyInformationHorizontalModule from 'bundles/unified-description-page-common/components/modules/key-information/KeyInformationHorizontalModule';
import ProductOverviewHorizontalModule from 'bundles/unified-description-page-common/components/modules/product-overview/ProductOverviewHorizontalModule';
import { HERO_BACKGROUND_COLOR } from 'bundles/unified-description-page-common/constants';
import { usePageNavigation } from 'bundles/unified-description-page-common/contexts/PageNavigationContext';
import type { InstructorFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCommonEntitiesFragments';
import { SPECIALIZATION_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';

import SkillPathwayIllustration from 'images/enterprise-learning-path-description-page/skill-pathway-illustration.png';

import _t from 'i18n!nls/enterprise-learning-path-description-page';

const styles = {
  link: css`
    /* override cds link color */
    color: var(--cds-color-neutral-primary) !important;
  `,
  flushBackgroundImage: css`
    position: absolute;
    top: 15px;
    right: 0;
    width: auto;
    z-index: -1;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

type Props = {
  pageData: EnterpriseLearningPathPageFieldsFragment;
  vspPageData: any;
};

const EnterpriseSkillPathwayProductOverviewModuleRenderer = ({ pageData, vspPageData }: Props) => {
  const { navigationLinks } = usePageNavigation();
  const thirdPartyOrg = useThirdPartyOrganization();
  const skillPathway = useSkillPathway();
  const instructors = getInstructorsFromVspPageData(vspPageData);
  const { totalAssignments, totalQuizzes } = getAssessmentsDataFromVspPageData(vspPageData);

  const enableVspXdpV2 = EnterpriseExperiments.get('enableVSPXDPV2');
  const keyInformationItems = [
    <KeyInformationDetailsItem
      title={
        <TrackedDiv trackingName="kim_view_syllabus">
          <Link
            href={`#${enableVspXdpV2 ? SKILL_PATHWAY_NAV_ITEMS.SKILL_GOALS : SPECIALIZATION_NAV_ITEMS.COURSES}`}
            css={styles.link}
          >
            {_t('#{courseCount} skill goals', {
              courseCount: skillPathway?.skillAreasWithUserData?.length || 0,
            })}
          </Link>
        </TrackedDiv>
      }
      subtitle={_t('Covers critical skills required at this career level ')}
      key="skill-goals"
    />,
    <SkillPathwayCareerBandKeyDetailsItem careerBand={skillPathway?.careerBand} key="career-band" />,
    <KeyInformationDetailsItem
      title={_t('Track your mastery')}
      subtitle={_t('Learn with comprehensive job-aligned assessments.')}
      key="skill-mastery"
    />,
    <KeyInformationDetailsItem
      title={_t('Flexible schedule')}
      subtitle={
        <React.Fragment>
          <div>{_t('2 months at 9 hours a week')}</div>
          <div>{_t('Learn at your own pace')}</div>
        </React.Fragment>
      }
      key="flexible-schedule"
    />,
  ];

  const smSpacing = enableVspXdpV2 ? 32 : 48;

  return (
    <React.Fragment>
      <ProductOverviewHorizontalModule
        disablePageNavigationDisplay={!enableVspXdpV2}
        navigationLinks={navigationLinks}
        anchorId={SKILL_PATHWAY_NAV_ITEMS.ABOUT}
        heroBackgroundColor={HERO_BACKGROUND_COLOR}
        overviewBackgroundImage={
          <Imgix
            src={SkillPathwayIllustration}
            alt={_t('Skill Pathway Background Illustration')}
            width="100%"
            height="376px"
            css={styles.flushBackgroundImage}
          />
        }
        hero={
          <HeroModule
            productTitle={pageData?.enterpriseBadge?.name}
            description={
              <Typography2 component="p" variant="bodyPrimary">
                {pageData?.enterpriseBadge?.description}
              </Typography2>
            }
            primaryCta={<EnterpriseSkillPathwayEnrollButtonNotConfigurable pageData={pageData} />}
            additionalInfo={
              thirdPartyOrg?.name &&
              !enableVspXdpV2 && (
                <Typography2 component="p" variant="bodySecondary" color="supportText">
                  {_t('Access provided by #{orgName}', {
                    orgName: thirdPartyOrg?.name,
                  })}
                </Typography2>
              )
            }
            instructors={
              enableVspXdpV2 ? <HeroInstructors instructors={instructors as Array<InstructorFragment>} /> : undefined
            }
            hideBackgroundImage
          />
        }
        keyInformation={
          <KeyInformationHorizontalModule keyInformationItems={keyInformationItems} disableMobileDivider />
        }
        aboutSubmodule={
          <Grid container item spacing={{ xs: 0, sm: smSpacing }} alignItems="center">
            <Grid item xs={12} sm={enableVspXdpV2 ? 12 : 8}>
              <LearningObjectives
                learningObjectives={pageData?.enterpriseBadge?.learningObjectives}
                moduleSpacing={32}
                checkSize="large"
              />
            </Grid>
            <Grid item xs={12} sm={enableVspXdpV2 ? 12 : 4}>
              <ToolsSection
                tools={
                  pageData?.enterpriseBadge?.skillsMetadata?.map((skill) => skill?.skillName || null).filter(Boolean) ||
                  []
                }
              />
            </Grid>
            {enableVspXdpV2 && (
              <Grid item xs={12}>
                <IncludedSection totalAssignments={totalAssignments} totalQuizzes={totalQuizzes + 5} />
              </Grid>
            )}
          </Grid>
        }
      />
    </React.Fragment>
  );
};

export default EnterpriseSkillPathwayProductOverviewModuleRenderer;
